import React from "react";
import { Tag, Sparkles, Clock, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { FIXED_PRODUCTS, CUSTOM_PRODUCTS } from "@/data/services";
import { Button } from "@/components/ui/button";

export const Pricing = ({ onQuote }) => {
  const { lang, tr } = useLang();
  const { content } = useContent();
  const kn = lang === "kn";

  return (
    <section id="pricing" className="py-16 lg:py-24 bg-slate-50 cp-grid-texture">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className={`text-xs font-bold uppercase tracking-widest text-amber-700 ${kn ? "font-kn" : ""}`}>{tr("pricing_kicker")}</p>
          <h2 className={`mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 ${kn ? "font-kn" : "font-heading"}`}>{tr("pricing_title")}</h2>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div data-testid="pricing-fixed" className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-green-100 text-green-700"><Tag size={20} /></span>
              <div>
                <h3 className={`text-lg font-bold text-slate-900 ${kn ? "font-kn" : "font-heading"}`}>{tr("pricing_fixed")}</h3>
                <p className={`text-sm text-slate-500 ${kn ? "font-kn" : ""}`}>{tr("pricing_fixed_sub")}</p>
              </div>
            </div>
            <div className="mt-5 divide-y divide-slate-100">
              {content.fixed_products.map((p, i) => {
                const m = FIXED_PRODUCTS[i] || {};
                return (
                  <div key={i} className="flex items-center justify-between gap-3 py-3.5">
                    <div>
                      <p className={`font-semibold text-slate-800 ${kn ? "font-kn" : ""}`}>{kn && m.name_kn ? m.name_kn : p.name}</p>
                      <p className={`text-xs text-slate-500 flex items-center gap-1.5 mt-0.5 ${kn ? "font-kn" : ""}`}>
                        {kn && m.unit_kn ? m.unit_kn : p.unit} · <Clock size={11} /> {kn && m.turn_kn ? m.turn_kn : p.turn}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-lg bg-green-50 px-3 py-1.5 text-base font-extrabold text-green-700">{p.price}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div data-testid="pricing-custom" className="rounded-2xl border-2 border-primary/30 bg-white p-6">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-lg bg-amber-100 text-amber-700"><Sparkles size={20} /></span>
              <div>
                <h3 className={`text-lg font-bold text-slate-900 ${kn ? "font-kn" : "font-heading"}`}>{tr("pricing_custom")}</h3>
                <p className={`text-sm text-slate-500 ${kn ? "font-kn" : ""}`}>{tr("pricing_custom_sub")}</p>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {content.custom_products.map((p, i) => {
                const m = CUSTOM_PRODUCTS[i] || {};
                return (
                  <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className={`font-semibold text-slate-800 ${kn ? "font-kn" : ""}`}>{kn && m.name_kn ? m.name_kn : p.name}</p>
                      <span className="shrink-0 text-right text-sm font-bold text-primary">
                        <span className={`block text-[10px] font-medium uppercase tracking-wide text-amber-600 ${kn ? "font-kn" : ""}`}>{tr("starting_from")}</span>
                        {p.price}
                      </span>
                    </div>
                    <p className={`mt-1 text-xs text-slate-500 ${kn ? "font-kn" : ""}`}>{kn && m.desc_kn ? m.desc_kn : p.desc}</p>
                  </div>
                );
              })}
            </div>
            <Button data-testid="pricing-custom-quote-btn" onClick={() => onQuote?.()} className="mt-5 w-full gap-2 bg-primary hover:bg-amber-700 text-white font-semibold">
              {tr("get_this_quote")} <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
